import { DataSource } from 'hooks/useHttpClient';
import { BasicMappedStatus } from './Candidate';
import { Comment, CommentListResolver } from './Comment';
import { FactAudit } from './Common';
import { FastMemberOption, MemberListFromTaskResolver } from './Members';
import { primary, warning, error } from 'lib/mui/colors';
import { Onboarding, OnboardingStages } from './Onboarding';
import { Document, DocumentListResolver } from './Document';

export type StatusWithColor = BasicMappedStatus & { color: string };

export const TaskTypes: BasicMappedStatus[] = [
    { id: 0, name: 'General Task' },
    { id: 1, name: 'Job Task' },
    { id: 2, name: 'Candidate Task' },
    { id: 3, name: 'Client Task' },
    { id: 4, name: 'Admin Task' },
    { id: 5, name: 'User Task' },
    { id: 6, name: 'Marketing Task' },
    { id: 7, name: 'Sales Task' },
    { id: 8, name: 'Finances Task' },
    { id: 9, name: 'Candidate Feedback' },
    { id: 10, name: 'Candidate Interview' },
    { id: 11, name: 'Candidate Testing' },
    { id: 12, name: 'Candidate Onboarding' },
    { id: 13, name: 'Resource Hardware' },
    { id: 14, name: 'SOW' }
];

export const TaskPriorities: StatusWithColor[] = [
    { id: 1, name: 'Low', color: primary[700] },
    { id: 2, name: 'Medium', color: warning[700] },
    { id: 3, name: 'High', color: error[700] }
];

export const TaskStatuses: BasicMappedStatus[] = [
    { id: 1, name: 'To Do' },
    { id: 2, name: 'In Progress' },
    { id: 3, name: 'Review' },
    { id: 4, name: 'Done' },
    { id: 5, name: 'Archive' }
];

export const DatesFilterOptions: BasicMappedStatus[] = [
    { id: 1, name: 'Overdue Tasks' },
    { id: 2, name: 'On Time' }
];

export const excludedStatuses = [5];

export type Task = {
    id: number;
    title: string;
    description: string;
    dueDate: string;
    priority: BasicMappedStatus;
    status: BasicMappedStatus;
    type: BasicMappedStatus;
    createdBy: FactAudit;
    members: FastMemberOption[];
    // TODO add attachments
    comments: Comment[];
    context?: {
        job?: BasicMappedStatus;
        candidate?: BasicMappedStatus;
        company?: BasicMappedStatus;
        user?: BasicMappedStatus;
    };
    // Onboarding tasks fields
    onboarding?: Onboarding;
    stage?: BasicMappedStatus;
    attachments: Document[];
};

const TaskHyrContextResolver = (apiResponse: any, context: string): BasicMappedStatus | undefined => {
    switch (context) {
        case 'job':
            if (apiResponse.job_requisition && apiResponse.job_requisition.length > 0) {
                return { id: apiResponse.job_requisition[0].id, name: apiResponse.job_requisition[0].title };
            }
            return undefined;
        case 'candidate':
            if (apiResponse.candidate && apiResponse.candidate.length > 0) {
                return {
                    id: apiResponse.candidate[0].id,
                    name: `${apiResponse.candidate[0].first_name} ${apiResponse.candidate[0].last_name}`
                };
            }
            return undefined;
        case 'company':
            if (apiResponse.job_requisition && apiResponse.job_requisition.length > 0) {
                return { id: apiResponse.job_requisition[0].company.id, name: apiResponse.job_requisition[0].company.name };
            }
            return undefined;
        case 'user':
            if (apiResponse.user && apiResponse.user.length > 0) {
                return { id: apiResponse.user[0].id, name: apiResponse.user[0].name };
            }
            return undefined;
        default:
            return undefined;
    }
};

export const TaskSingleResolver = (dataSource: DataSource, apiResponse: any): Task | null => {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    id: apiResponse.id,
                    title: apiResponse.title,
                    description: apiResponse.description,
                    dueDate: apiResponse.due_date,
                    priority: TaskPriorities.find((priority) => priority.id === +apiResponse.priority) || TaskPriorities[0],
                    status: TaskStatuses.find((status) => status.id === apiResponse.status_id) || TaskStatuses[0],
                    type: TaskTypes.find((type) => type.id === apiResponse.type_id) || TaskTypes[0],
                    createdBy: {
                        userId: apiResponse.creator?.id || 0,
                        dateStr: apiResponse.created_at,
                        userFullName: apiResponse.creator?.name || '',
                        avatar: apiResponse.creator?.profile?.profile_picture || ''
                    },
                    members: MemberListFromTaskResolver(dataSource, apiResponse.members),
                    comments: CommentListResolver(dataSource, apiResponse.comments, true),
                    context: {
                        job: TaskHyrContextResolver(apiResponse, 'job'),
                        candidate: TaskHyrContextResolver(apiResponse, 'candidate'),
                        company: TaskHyrContextResolver(apiResponse, 'company'),
                        user: TaskHyrContextResolver(apiResponse, 'user')
                    },
                    onboarding: apiResponse.onboarding ? ({ id: apiResponse.onboarding.id } as Onboarding) : undefined,
                    stage: OnboardingStages.find((status) => status.id === apiResponse.stage_id),
                    attachments: DocumentListResolver(dataSource, apiResponse.attachments).map((doc: Document) => ({
                        ...doc,
                        url: `tasks/attachments/${doc.id}`
                    }))
                };

            default:
                return null;
        }
    } catch (errora) {
        console.log(errora);
        return null;
    }
};

export const NastyTaskListResolver = (dataSource: DataSource, apiResponse: any): Task[] => {
    try {
        switch (dataSource) {
            case 'hyrerocket': {
                const tasks: Task[] = [];
                Object.keys(apiResponse).forEach((key) => {
                    const currentStatus = TaskStatuses.find((status) => status.name === key);
                    if (currentStatus && !excludedStatuses.includes(currentStatus.id)) {
                        const currentTasks = apiResponse[key][0] || undefined;
                        if (currentTasks) {
                            const taskLists = Object.values(currentTasks)
                                .map((task: any) => TaskSingleResolver(dataSource, task))
                                .filter((task: Task | null) => !!task) as Task[];

                            tasks.push(...taskLists);
                        }
                    }
                });
                return tasks;
            }

            default:
                return [];
        }
    } catch (errora) {
        console.log(errora);
        return [];
    }
};

export const FastTaskListResolver = (dataSource: DataSource, apiResponse: any): Task[] => {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return apiResponse.map((task: any) => ({
                    id: task.id,
                    title: task.title,
                    dueDate: task.due_date,
                    priority: TaskPriorities.find((priority) => priority.id === +task.priority) || TaskPriorities[0],
                    status: TaskStatuses.find((status) => status.id === task.status) || TaskStatuses[0],
                    type: TaskTypes.find((type) => type.id === task.type) || TaskTypes[0],
                    createdBy: {
                        userId: 0,
                        dateStr: task.due_date,
                        userFullName: task.asignee || '',
                        avatar: task.profile_picture || ''
                    },
                    members: [],
                    comments: [],
                    job: undefined,
                    candidate: undefined,
                    company: undefined,
                    onboarding: undefined,
                    stage: undefined
                }));

            default:
                return [];
        }
    } catch (errora) {
        console.log(errora);
        return [];
    }
};
